// src/components/Toast/Toast.tsx
import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastProps {
  message?: React.ReactNode;
  type: ToastType;
  duration?: number;
  onClose: () => void;
  children?: React.ReactNode;
}

const Toast: React.FC<ToastProps> = ({ message, type, duration = 4000, onClose, children }) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300);
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const getBgColor = () => {
    switch (type) {
      case 'success':
        return '#4caf50';
      case 'error':
        return '#f44336';
      case 'warning':
        return '#ff9800';
      default:
        return '#2196f3';
    }
  };

  if (!isVisible) return null;

  const content = children || message;
  const displayContent =
    typeof content === 'string' || React.isValidElement(content) ? content : String(content);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: getBgColor(),
        color: 'white',
        padding: '12px 24px',
        borderRadius: '4px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '300px',
        maxWidth: '90%',
        animation: 'slideIn 0.3s ease-out',
        '@keyframes slideIn': {
          from: { transform: 'translate(-50%, -100%)', opacity: 0 },
          to: { transform: 'translate(-50%, 0)', opacity: 1 },
        },
        '&.exiting': {
          animation: 'fadeOut 0.3s ease-in',
          '@keyframes fadeOut': {
            from: { opacity: 1 },
            to: { opacity: 0 },
          },
        },
      }}
      className={!isVisible ? 'exiting' : ''}
    >
      <Typography component="div" variant="body1">
        {displayContent}
      </Typography>
    </Box>
  );
};

export default Toast;
